/**
 * Pure helpers behind the TradeFinder trading UX: the signal strip, level
 * confluence emphasis, edge dimming, the recency fade, and the alert filter
 * shared by the browser chime and (mirrored in Rust, utt-engine
 * flow_signals/alerts.rs) the phone push. Visual / notification only.
 */
import type { KeyLevel, KeyLevelKind } from '../../utils/keyLevels';
import {
  EDGE_T_RELIABLE,
  flowReading,
  flowToggleKey,
  isFlowContextKind,
  type FlowReading,
  type FlowSignal,
} from '../../store/flowSignalsStore';
import { FLOW_GLYPHS } from '../Chart/FlowSignalMarkerPrimitive';

// ------------------------------------------------------------- levels

/**
 * Short level codes, the same ones the engine's absorption markers use
 * (LevelKind::code in utt-engine/src/absorption/mod.rs) plus wPOC, which the
 * chart draws and the engine does not test.
 */
export const KEY_LEVEL_CODES: Record<KeyLevelKind, string> = {
  putWall: 'PW',
  callWall: 'CW',
  zeroGamma: 'ZG',
  ivUp: 'IV+',
  ivDown: 'IV-',
  poc: 'POC',
  vah: 'VAH',
  val: 'VAL',
  wpoc: 'wPOC',
  wvah: 'wVAH',
  wval: 'wVAL',
  onh: 'ONH',
  onl: 'ONL',
  pdh: 'PDH',
  pdl: 'PDL',
  moc: 'MOC',
};

/** Roots TradeFinder covers. */
export const TF_ROOTS = ['MNQ', 'NQ', 'ES', 'MES'] as const;
export type TfRoot = (typeof TF_ROOTS)[number];

/** Points: a Nasdaq tick is 0.25 of a ~30k index, an S&P one of a ~7k index. */
export const DEFAULT_LEVEL_TOL: Record<TfRoot, number> = { NQ: 3, MNQ: 3, ES: 1, MES: 1 };

export function levelTolFor(root: string, tol: Partial<Record<string, number>> = DEFAULT_LEVEL_TOL): number {
  const v = tol[root];
  if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return v;
  return (DEFAULT_LEVEL_TOL as Record<string, number>)[root] ?? 0;
}

export interface LevelMatch {
  code: string;
  kind: KeyLevelKind;
  price: number;
  /** |signal price - level price|, points. */
  dist: number;
}

/**
 * The key level nearest `price` within `tol` points (inclusive), or null.
 * Ties go to the earlier level in `levels` so the answer is deterministic.
 */
export function nearestLevel(price: number, levels: readonly KeyLevel[], tol: number): LevelMatch | null {
  if (!Number.isFinite(price) || !(tol >= 0)) return null;
  let best: LevelMatch | null = null;
  for (const l of levels) {
    if (!Number.isFinite(l.price) || l.price <= 0) continue;
    const dist = Math.abs(price - l.price);
    // A hair of float slack: 30772.25 vs a level 3.00 away is "within 3".
    if (dist > tol + 1e-9) continue;
    if (!best || dist < best.dist) best = { code: KEY_LEVEL_CODES[l.kind] ?? l.label, kind: l.kind, price: l.price, dist };
  }
  return best;
}

/** "🍪 at PW". */
export function levelTag(s: Pick<FlowSignal, 'kind'>, code: string): string {
  return `${tfGlyph(s.kind)} at ${code}`;
}

// ------------------------------------------------------------- edge

/** The tuner's t for a signal: band_t (1-5 min band), else the legacy 300 s t; null when absent. */
export function signalEdgeT(detail: FlowSignal['detail']): number | null {
  const e = detail?.edge as Record<string, unknown> | undefined;
  if (!e || typeof e !== 'object') return null;
  const band = e.band_t;
  if (typeof band === 'number' && Number.isFinite(band)) return band;
  const t = e.t;
  if (typeof t === 'number' && Number.isFinite(t)) return t;
  return null;
}

export const isReliableT = (t: number | null, min = EDGE_T_RELIABLE): boolean => t !== null && Math.abs(t) >= min;

/**
 * The CURRENT edge t of every kind for one root: the newest signal of that
 * kind carries the tuner's reading at the time it fired, so it is what the
 * service believes now. `signals` ascending by time (store order). A kind
 * whose newest signal has no edge maps to null.
 */
export function currentEdgeByKind(signals: readonly FlowSignal[]): Map<string, number | null> {
  const out = new Map<string, number | null>();
  for (let i = signals.length - 1; i >= 0; i -= 1) {
    const s = signals[i];
    if (!out.has(s.kind)) out.set(s.kind, signalEdgeT(s.detail));
  }
  return out;
}

// ------------------------------------------------------------- glyph / text

export function tfGlyph(kind: string): string {
  return FLOW_GLYPHS[kind]?.symbol ?? '○';
}

const KIND_NAMES: Record<string, string> = {
  absorption: 'absorption',
  sweep: 'sweep',
  passive_sweep_trace: 'passive sweep',
  stop_sweep_trace: 'stop sweep',
  stop_run: 'stop run',
  stop_volume: 'stop volume',
  volume_spike: 'volume spike',
  imbalance_signal: 'imbalance',
  iceberg: 'iceberg',
  iceberg_development: 'iceberg',
  iceberg_cancelled: 'iceberg cancelled',
  book_imbalance: 'book imbalance',
  liquidity: 'liquidity',
  sweep_follow: 'sweep follow',
  burst_level: 'burst into level',
  abs_break_level: 'absorption break',
  queue_depletion: 'queue depletion',
};

export function kindName(kind: string): string {
  return KIND_NAMES[kind] ?? kind.replace(/_/g, ' ');
}

/** "Buy" / "Sell" (the signal's side as flow-signals labels it). */
export function sideLabel(side: string): string {
  return side === 'buy' ? 'Buy' : side === 'sell' ? 'Sell' : side;
}

/** One decimal with a real minus sign. */
export function fmtT(t: number): string {
  const r = Math.round(t * 10) / 10;
  return (r < 0 ? '−' : '') + Math.abs(r).toFixed(1);
}

/** Age counter: "8s", "95s" up to 2 minutes, then "4m", then "2h". */
export function fmtAge(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 120) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 120) return `${m}m`;
  return `${Math.floor(m / 60)}h`;
}

// ------------------------------------------------------------- strip

export interface StripEntry {
  signal: FlowSignal;
  glyph: string;
  side: string;
  reading: FlowReading;
  ageMs: number;
  edgeT: number | null;
  reliable: boolean;
  level: LevelMatch | null;
  /** Context kind (sweep_follow): no tuner edge by design; the strip says "context" instead of "no edge data". */
  context: boolean;
}

/**
 * Latest `n` signals of the kinds that are shown, newest first, with what the
 * strip prints for each. `signals` ascending by time.
 */
export function stripEntries(
  signals: readonly FlowSignal[],
  shown: Partial<Record<string, boolean>>,
  n: number,
  now: number,
  levels: readonly KeyLevel[],
  tol: number,
): StripEntry[] {
  const out: StripEntry[] = [];
  for (let i = signals.length - 1; i >= 0 && out.length < n; i -= 1) {
    const s = signals[i];
    const key = flowToggleKey(s.kind);
    if (!key || !shown[key]) continue;
    const edgeT = signalEdgeT(s.detail);
    out.push({
      signal: s,
      glyph: tfGlyph(s.kind),
      side: sideLabel(s.side),
      reading: flowReading(s),
      ageMs: Math.max(0, now - s.timestamp),
      edgeT,
      reliable: isReliableT(edgeT),
      level: nearestLevel(s.price, levels, tol),
      context: isFlowContextKind(s.kind),
    });
  }
  return out;
}

// ------------------------------------------------------------- fade / dim

/** Markers stay at full strength this long by default, ms (tradeFinderUxStore.fadeStartMin). */
export const FADE_FULL_MS = 10 * 60_000;
/** ...then fade linearly to FADE_FLOOR by this age, ms. */
export const FADE_END_MS = 60 * 60_000;
export const FADE_FLOOR = 0.7;
/** Opacity of a kind without a reliable edge. */
export const DIM_ALPHA = 0.65;
/** The newest marker pulses this long after it fires, ms. */
export const PULSE_MS = 8_000;

/** Recency opacity: 1 until `fullMs`, then linear down to FADE_FLOOR at FADE_END_MS (or fullMs + 50 min). */
export function fadeAlpha(ageMs: number, fullMs: number = FADE_FULL_MS): number {
  const endMs = Math.max(FADE_END_MS, fullMs + 50 * 60_000);
  if (!(ageMs > fullMs)) return 1;
  if (ageMs >= endMs) return FADE_FLOOR;
  const k = (ageMs - fullMs) / (endMs - fullMs);
  return 1 - k * (1 - FADE_FLOOR);
}

export interface MarkerStyleOptions {
  now: number;
  fade: boolean;
  dimNoEdge: boolean;
  emphasis: boolean;
  pulse: boolean;
  levels: readonly KeyLevel[];
  tol: number;
  /** currentEdgeByKind for this root. */
  edgeByKind: ReadonlyMap<string, number | null>;
  /** Id of the newest shown signal (the one that may pulse). */
  newestId: string | null;
  /** Fade starts at this age, ms (default FADE_FULL_MS). */
  fadeStartMs?: number;
}

export interface MarkerStyle {
  /** 0..1, never below FADE_FLOOR * DIM_ALPHA. */
  alpha: number;
  emphasis: boolean;
  tag: string | undefined;
  pulse: boolean;
}

/** How one marker is drawn (opacity, emphasis + level tag, pulse). */
export function markerStyle(s: FlowSignal, o: MarkerStyleOptions): MarkerStyle {
  let alpha = o.fade ? fadeAlpha(o.now - s.timestamp, o.fadeStartMs) : 1;
  // Only a MEASURED weak edge dims: no edge data is "unknown", drawn as normal.
  // Context kinds carry held-out stats, not a tuner edge: never dimmed for it.
  const t = o.edgeByKind.get(s.kind);
  if (o.dimNoEdge && !isFlowContextKind(s.kind) && typeof t === 'number' && !isReliableT(t)) alpha *= DIM_ALPHA;
  const match = o.emphasis ? nearestLevel(s.price, o.levels, o.tol) : null;
  const age = o.now - s.timestamp;
  return {
    alpha: Math.max(0, Math.min(1, alpha)),
    emphasis: match !== null,
    tag: match ? match.code : undefined,
    pulse: o.pulse && s.id === o.newestId && age >= 0 && age < PULSE_MS,
  };
}

// ------------------------------------------------------------- alert filter

/** Kinds that can alert (traces and iceberg development/cancel never do). */
export const ALERT_KINDS = [
  'absorption', 'sweep', 'stop_run', 'stop_volume', 'volume_spike',
  'imbalance_signal', 'iceberg', 'book_imbalance', 'liquidity', 'sweep_follow',
] as const;
export type AlertKind = (typeof ALERT_KINDS)[number];

export interface QuietHours {
  enabled: boolean;
  /** "HH:MM" in `tz`. */
  start: string;
  end: string;
  tz: string;
}

/** GET/PUT /api/flow-alerts/settings (utt-engine flow_signals/alerts.rs). */
export interface FlowAlertSettings {
  enabled: boolean;
  kinds: Record<AlertKind, boolean>;
  symbols: string[];
  side: 'buy' | 'sell' | 'both';
  minEdgeT: number;
  allowMissingEdge: boolean;
  atLevelOnly: boolean;
  levelTolPts: Record<string, number>;
  minValue: number;
  cooldownSec: number;
  quietHours: QuietHours;
  maxPerMinute: number;
}

export const DEFAULT_ALERT_SETTINGS: FlowAlertSettings = {
  enabled: true,
  kinds: {
    absorption: true, sweep: true, stop_run: true, stop_volume: false, volume_spike: false,
    imbalance_signal: false, iceberg: false, book_imbalance: false, liquidity: false,
    // Context (unproven): never alerts unless asked for (engine default false too).
    // burst_level / abs_break_level / queue_depletion are not in ALERT_KINDS at all: display only.
    sweep_follow: false,
  },
  symbols: ['MNQ', 'NQ', 'ES', 'MES'],
  side: 'both',
  minEdgeT: 2.0,
  allowMissingEdge: false,
  atLevelOnly: true,
  levelTolPts: { ...DEFAULT_LEVEL_TOL },
  minValue: 0,
  cooldownSec: 300,
  quietHours: { enabled: false, start: '13:15', end: '06:30', tz: 'America/Los_Angeles' },
  maxPerMinute: 6,
};

const clampNum = (v: unknown, lo: number, hi: number, def: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : def;
const isHHMM = (v: unknown): v is string => typeof v === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(v);

/** Any engine reply (or a partial object) -> complete, clamped settings. */
export function normalizeAlertSettings(raw: unknown): FlowAlertSettings {
  const d = DEFAULT_ALERT_SETTINGS;
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const kindsIn = (r.kinds && typeof r.kinds === 'object' ? r.kinds : {}) as Record<string, unknown>;
  const kinds = { ...d.kinds };
  for (const k of ALERT_KINDS) if (typeof kindsIn[k] === 'boolean') kinds[k] = kindsIn[k] as boolean;
  const symbols = Array.isArray(r.symbols)
    ? r.symbols.filter((s): s is string => typeof s === 'string' && (TF_ROOTS as readonly string[]).includes(s))
    : d.symbols.slice();
  const tolIn = (r.levelTolPts && typeof r.levelTolPts === 'object' ? r.levelTolPts : {}) as Record<string, unknown>;
  const levelTolPts: Record<string, number> = {};
  for (const root of TF_ROOTS) levelTolPts[root] = clampNum(tolIn[root], 0, 50, DEFAULT_LEVEL_TOL[root]);
  const q = (r.quietHours && typeof r.quietHours === 'object' ? r.quietHours : {}) as Record<string, unknown>;
  return {
    enabled: typeof r.enabled === 'boolean' ? r.enabled : d.enabled,
    kinds,
    symbols,
    side: r.side === 'buy' || r.side === 'sell' || r.side === 'both' ? r.side : d.side,
    minEdgeT: clampNum(r.minEdgeT, 0, 10, d.minEdgeT),
    allowMissingEdge: typeof r.allowMissingEdge === 'boolean' ? r.allowMissingEdge : d.allowMissingEdge,
    atLevelOnly: typeof r.atLevelOnly === 'boolean' ? r.atLevelOnly : d.atLevelOnly,
    levelTolPts,
    minValue: clampNum(r.minValue, 0, 1e9, d.minValue),
    cooldownSec: Math.round(clampNum(r.cooldownSec, 0, 86_400, d.cooldownSec)),
    quietHours: {
      enabled: typeof q.enabled === 'boolean' ? q.enabled : d.quietHours.enabled,
      start: isHHMM(q.start) ? q.start : d.quietHours.start,
      end: isHHMM(q.end) ? q.end : d.quietHours.end,
      tz: typeof q.tz === 'string' && q.tz.length > 0 ? q.tz : d.quietHours.tz,
    },
    maxPerMinute: Math.round(clampNum(r.maxPerMinute, 1, 60, d.maxPerMinute)),
  };
}

/** Minute of day of `ms` in `tz` (falls back to the local zone on a bad tz). */
export function minuteOfDay(ms: number, tz: string): number {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(ms);
  } catch {
    const d = new Date(ms);
    return d.getHours() * 60 + d.getMinutes();
  }
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? 0) % 24;
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  return h * 60 + m;
}

const hhmm = (s: string): number => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));

/** Inside the quiet window? Start inclusive, end exclusive; a window may wrap midnight. */
export function inQuietHours(q: QuietHours, now: number): boolean {
  if (!q.enabled) return false;
  const a = hhmm(q.start);
  const b = hhmm(q.end);
  if (a === b) return false;
  const m = minuteOfDay(now, q.tz);
  return a < b ? m >= a && m < b : m >= a || m < b;
}

export type AlertSkip =
  | 'disabled' | 'stale' | 'kind' | 'symbol' | 'side' | 'value' | 'edge' | 'level' | 'quiet' | 'cooldown' | 'rate';

/** Mutable per-consumer state (one per browser tab for sound; the engine keeps its own). */
export interface AlertState {
  lastByKey: Map<string, number>;
  sent: number[];
}

export const newAlertState = (): AlertState => ({ lastByKey: new Map(), sent: [] });

/** Signals older than this are history, never alerts (the engine's LIVE_MAX_AGE_MS). */
export const ALERT_LIVE_MS = 60_000;

/**
 * The same gates, in the same order, as the engine's push rules. Returns the
 * level match (or null) when the signal passes, else the reason it was
 * skipped. On a pass `state` records the send (cooldown + rate cap).
 */
export function evaluateAlert(
  s: FlowSignal,
  settings: FlowAlertSettings,
  levels: readonly KeyLevel[] | null,
  state: AlertState,
  now: number,
  extraKindGate?: (kind: string) => boolean,
): { ok: true; level: LevelMatch | null } | { ok: false; skip: AlertSkip } {
  if (!settings.enabled) return { ok: false, skip: 'disabled' };
  if (now - s.timestamp > ALERT_LIVE_MS) return { ok: false, skip: 'stale' };
  if (!(ALERT_KINDS as readonly string[]).includes(s.kind) || !settings.kinds[s.kind as AlertKind]) return { ok: false, skip: 'kind' };
  if (extraKindGate && !extraKindGate(s.kind)) return { ok: false, skip: 'kind' };
  const root = s.root || '';
  if (!settings.symbols.includes(root)) return { ok: false, skip: 'symbol' };
  if (settings.side !== 'both' && s.side !== settings.side) return { ok: false, skip: 'side' };
  if (settings.minValue > 0 && Math.abs(s.value) < settings.minValue) return { ok: false, skip: 'value' };
  // Context kinds have no tuner edge by design: the edge gate never applies (mirrors CONTEXT_KINDS in engine alerts.rs).
  const t = signalEdgeT(s.detail);
  if (!isFlowContextKind(s.kind) && (t === null ? !settings.allowMissingEdge : Math.abs(t) < settings.minEdgeT)) {
    return { ok: false, skip: 'edge' };
  }
  const level = levels ? nearestLevel(s.price, levels, levelTolFor(root, settings.levelTolPts)) : null;
  if (settings.atLevelOnly && !level) return { ok: false, skip: 'level' };
  if (inQuietHours(settings.quietHours, now)) return { ok: false, skip: 'quiet' };
  const key = `${s.kind}|${root}`;
  const last = state.lastByKey.get(key);
  if (last !== undefined && now - last < settings.cooldownSec * 1000) return { ok: false, skip: 'cooldown' };
  while (state.sent.length > 0 && now - state.sent[0] >= 60_000) state.sent.shift();
  if (state.sent.length >= settings.maxPerMinute) return { ok: false, skip: 'rate' };
  state.lastByKey.set(key, now);
  state.sent.push(now);
  return { ok: true, level };
}

// ------------------------------------------------------------- deep link

/**
 * `#MNQ` (or `#mnq`, `#MNQZ6`) -> the active contract with that base, or
 * null when the hash names nothing the engine streams. Never falls back to
 * another symbol: a bad link must not switch the chart.
 */
export function symbolFromHash(hash: string, activeSymbols: readonly string[], contractBase: (s: string) => string): string | null {
  let want: string;
  try {
    want = decodeURIComponent((hash || '').replace(/^#/, '')).trim().toUpperCase();
  } catch {
    return null; // malformed escape (#%E0%A4%A)
  }
  if (!want || !/^[A-Z0-9._-]{1,20}$/.test(want)) return null;
  const exact = activeSymbols.find((s) => s.toUpperCase() === want);
  if (exact) return exact;
  const base = contractBase(want);
  return activeSymbols.find((s) => contractBase(s) === base) ?? null;
}
